// Libs
import classNames from 'classnames/bind';
import {
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BasePagination } from '~/components/common';
// Others
import { IHeaderCellType } from '~/utils/interface/common';
import { DEFAULT_NUMBER_ZERO } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseTable.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  count?: number;
  columns?: IHeaderCellType<T>[];
  dataSource?: T[];
  currentPage?: number;
  currentRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  dataSelectedIds?: T[keyof T][];
  onPageChange?: (event: ChangeEvent<unknown>, page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onRowClick?: (id: keyof T, record: T) => void;
  onSelectChange?: (selectedIds: (keyof T)[]) => void;
};

const cx = classNames.bind(styles);

const BaseTable = <T extends Record<string, any>>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    count,
    columns = [],
    dataSource = [],
    currentPage,
    currentRowsPerPage,
    rowsPerPageOptions,
    onRowsPerPageChange,
    onPageChange,
    onRowClick,
    onSelectChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectedIds, setSelectedIds] = useState<(keyof T)[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (props.dataSelectedIds !== undefined) {
      setSelectedIds(props.dataSelectedIds);
    }
  }, [props.dataSelectedIds]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleRowClick = (event: React.MouseEvent<HTMLElement>, id: keyof T, record: T) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
      return;
    }

    onRowClick && onRowClick(id, record);
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = dataSource.map((row) => row.id as keyof T);
      setSelectedIds(allIds);
      onSelectChange && onSelectChange(allIds);
    } else {
      setSelectedIds([]);
      onSelectChange && onSelectChange([]);
    }
  };

  const handleSelectRow = (event: ChangeEvent<HTMLInputElement>, id: keyof T) => {
    const isSelected = selectedIds.includes(id);
    const updatedSelectedIds = isSelected
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];

    setSelectedIds(updatedSelectedIds);
    onSelectChange && onSelectChange(updatedSelectedIds);
  };

  const isRowSelected = (id: keyof T) => selectedIds.includes(id);
  //#endregion Handle Function

  return (
    <Stack component={'div'} id='baseTableComponent' className={cx('container')} gap={'20px'}>
      <TableContainer component={Paper} className={cx('paper')}>
        <Table padding='none' size='medium'>
          <TableHead>
            <TableRow className={cx('tableHeadRow')}>
              {onSelectChange && (
                <TableCell className={cx('tableHeadCell', 'tableHeadCellCheckbox')} padding='checkbox'>
                  <Checkbox
                    size='small'
                    icon={
                      <img src={icons.tableIconUncheck} alt={t('altImg')} className={cx('checkboxIcon')} />
                    }
                    checkedIcon={
                      <img src={icons.tableIconChecked} alt={t('altImg')} className={cx('checkboxIcon')} />
                    }
                    indeterminateIcon={
                      <img
                        src={icons.tableIconIndeterminate}
                        alt={t('altImg')}
                        className={cx('checkboxIcon')}
                      />
                    }
                    className={cx('checkbox')}
                    color='primary'
                    indeterminate={
                      selectedIds.length > DEFAULT_NUMBER_ZERO && selectedIds.length < dataSource.length
                    }
                    checked={
                      dataSource.length > DEFAULT_NUMBER_ZERO && selectedIds.length === dataSource.length
                    }
                    onChange={handleSelectAll}
                    inputProps={{
                      'aria-label': 'select all desserts',
                    }}
                  />
                </TableCell>
              )}

              {columns?.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    width: column.width,
                    ...(column.id === 'action' && { width: '57px' }),
                  }}
                  className={cx('tableHeadCell')}
                  variant='head'
                  color='primary'
                >
                  {column?.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {dataSource?.length === DEFAULT_NUMBER_ZERO ? (
              <TableRow>
                <TableCell colSpan={columns?.length} align='center' className={cx('tableBodyCellNoData')}>
                  {t('common_no_data_available')}
                </TableCell>
              </TableRow>
            ) : (
              dataSource?.map((row, index) => {
                const isItemSelected = selectedIds.includes(row.id);

                return (
                  <TableRow
                    key={index}
                    hover
                    selected={isItemSelected}
                    tabIndex={-1}
                    className={cx('tableBodyRow', { tableRowClick: onRowClick })}
                    onClick={(event) => handleRowClick(event, row.id as keyof T, row)}
                  >
                    {onSelectChange && (
                      <TableCell
                        variant='body'
                        className={cx('tableBodyCell', 'tableHeadCellCheckbox')}
                        padding='checkbox'
                      >
                        <Checkbox
                          icon={
                            <img
                              src={icons.tableIconUncheck}
                              alt={t('altImg')}
                              className={cx('checkboxIcon')}
                            />
                          }
                          checkedIcon={
                            <img
                              src={icons.tableIconChecked}
                              alt={t('altImg')}
                              className={cx('checkboxIcon')}
                            />
                          }
                          indeterminateIcon={
                            <img
                              src={icons.tableIconIndeterminate}
                              alt={t('altImg')}
                              className={cx('checkboxIcon')}
                            />
                          }
                          size='small'
                          className={cx('checkbox')}
                          checked={isRowSelected(row.id as keyof T)}
                          onChange={(event) => handleSelectRow(event, row.id as keyof T)}
                        />
                      </TableCell>
                    )}

                    {columns?.map((column) => {
                      const value = row[column.id as keyof T];

                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          variant='body'
                          className={cx('tableBodyCell')}
                        >
                          {column.render
                            ? column.render(row)
                            : column?.format && typeof value === 'number'
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dataSource?.length > DEFAULT_NUMBER_ZERO && onPageChange && (
        <BasePagination
          count={count}
          page={currentPage}
          currentRowsPerPage={currentRowsPerPage}
          onChange={onPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </Stack>
  );
};

export default BaseTable;
